// New carousel component using swiper.js

import React, { Key, useEffect, useRef, useState } from "react"
import Swiper from "swiper"
import { Navigation, Pagination, Autoplay } from "swiper/modules"
import "swiper/css/bundle"
import { Link } from "gatsby"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import { Button } from "primereact/button"
import CustomDropdown from "../customDropdownBtn/customDropdown"
import arrow from "./../../images/arrow-right-white.svg"
import "./NewCarousel.scss"

const NewCarousel = ({ data, lazyLoad = false }) => {
  const [isHover, setIsHover] = useState<boolean>(false)
  const [selectedIndustry, setSelectedIndustry] = useState("")
  const [selectedTechnology, setSelectedTechnology] = useState("")
  const [openIndustry, setOpenIndustry] = useState<boolean>(false)
  const [openTechnology, setOpenTechnology] = useState<boolean>(false)
  const [filteredIndustries, setFilteredIndustries] = useState<any>([])
  const [filteredTechnologies, setFilteredTechnologies] = useState<any>([])
  const [finalFilteredData, setFinalFilteredData] = useState(data)

  const swiperContainerRef = useRef<HTMLDivElement | null>(null)
  const swiperInstanceRef = useRef<Swiper | null>(null)

  const toggleAutoplay = shouldAutoplay => {
    if (swiperInstanceRef.current) {
      if (shouldAutoplay) {
        swiperInstanceRef.current.autoplay.start()
      } else {
        swiperInstanceRef.current.autoplay.stop()
      }
    }
  }

  useEffect(() => {
    if (swiperContainerRef.current) {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.update()
      } else {
        swiperInstanceRef.current = new Swiper(swiperContainerRef.current, {
          modules: [Navigation, Pagination, Autoplay],
          direction: "horizontal",
          loop: finalFilteredData.length > 1,
          autoplay: {
            delay: 3000,
          },
          pagination: {
            el: ".swiper-pagination",
            clickable: true,
          },
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          allowSlideNext: true,
          allowSlidePrev: true,
          allowTouchMove: true,
          slidesPerView: 1,
          createElements: true,
        })
      }
    }
  }, [finalFilteredData])

  useEffect(() => {
    if (selectedTechnology) {
      const industries = data
        ?.filter(item => item?.technology === selectedTechnology)
        ?.map(item => item?.industry)
      setFilteredIndustries([...new Set(industries)])
    } else {
      setFilteredIndustries([...new Set(data?.map(item => item?.industry))])
    }
  }, [selectedTechnology, data])

  useEffect(() => {
    if (selectedIndustry) {
      const technologies = data
        ?.filter(item => item?.industry === selectedIndustry)
        ?.map(item => item.technology)
      setFilteredTechnologies([...new Set(technologies)])
    } else {
      setFilteredTechnologies([...new Set(data?.map(item => item?.technology))])
    }
  }, [selectedIndustry, data])

  useEffect(() => {
    const filtered = data?.filter(item => {
      return (
        (!selectedIndustry || item?.industry === selectedIndustry) &&
        (!selectedTechnology || item?.technology === selectedTechnology)
      )
    })
    setFinalFilteredData(filtered)
  }, [selectedIndustry, selectedTechnology, data])

  const handleIndustryChange = value => {
    setSelectedIndustry(value)
    // setSelectedTechnology("")
  }

  const handleTechnologyChange = value => {
    setSelectedTechnology(value)
    // setSelectedIndustry("")
  }

  const convertBracketsToSpan = text => {
    if (!text) return text
    return text?.replace(/\[(.*?)\]/g, "<span>$1</span>")
  }

  return (
    <div
      className="swiper"
      ref={swiperContainerRef}
      onMouseEnter={() => {
        setIsHover(true)
        toggleAutoplay(false)
      }}
      onMouseLeave={() => {
        setIsHover(false)
        setOpenIndustry(false)
        setOpenTechnology(false)
        toggleAutoplay(true)
      }}
    >
      <div className="swiper-wrapper">
        {finalFilteredData?.map((card, key: Key) => (
          <div
            className="swiper-slide"
            key={key}
            // style={{
            //   backgroundImage: `url(${card?.mainImage?.asset?.url})`,
            // }}
          >
            <img
              src={card?.mainImage?.asset?.url}
              alt={card.cardTitle}
              className="carousel-bg-image"
              loading={lazyLoad === true ? "lazy" : "eager"}
            />
            <div className="backdrop-filter" />

            {/* Conditionally display content based on mouse hover state within the carousel. */}
            {/* {!isHover ? (
              <div className="slide-content">
                <div className="slide-info">
                  <h2>{card.cardTitle}</h2>
                </div>
                <div className="slide-main-cta button-container">
                  <Link
                    className="button-cl connectBtn"
                    to={`/${card?.slug?.current}`}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Find out how
                  </Link>
                </div>
              </div>
            ) : ( */}
            <div className="slide-content2">
              <div className="slide-info">
                <h2>{card.cardTitle}</h2>
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {convertBracketsToSpan(card?.content)}
                </ReactMarkdown>
              </div>
              <div className="slide-main-cta button-container">
                {typeof card?.slug?.current !== "undefined" && (
                  <Link
                    className="button-cl connectBtn"
                    to={`/${card?.slug?.current}`}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Find out how
                  </Link>
                )}
              </div>
            </div>
            {/* )} */}
          </div>
        ))}
      </div>

      {/* Carousel navigation buttons */}
      <>
        <Button
          disabled={finalFilteredData.length <= 1}
          title="previous slide"
          className={`swiper-button-prev p-button-secondary ${
            finalFilteredData.length <= 1 ? "btn-disabled" : ""
          }`}
          icon={<img src={arrow} alt="" />}
          onClick={() => (swiperContainerRef.current as any).swiper.slideNext()}
          rounded
        />
        <Button
          disabled={finalFilteredData.length <= 1}
          title="next slide"
          className={`swiper-button-next p-button-secondary ${
            finalFilteredData.length <= 1 ? "btn-disabled" : ""
          }`}
          icon={<img src={arrow} alt="" />}
          onClick={() => (swiperContainerRef.current as any).swiper.slideNext()}
          rounded
        />
      </>

      {/* Display the custom dropdown menus when mouse is hovering on the carousel */}
      {isHover ? (
        <div className="carousel-header">
          <div className="header-title">SUCCESS STORIES</div>
          <div className="dropdown-options">
            <CustomDropdown
              options={["All", ...filteredIndustries]}
              value={selectedIndustry}
              onChange={handleIndustryChange}
              placeholder="Industry"
              openCurrentDropdown={openIndustry}
              setOpenDropdown={setOpenIndustry}
              setOtherDropdown={setOpenTechnology}
            />

            <CustomDropdown
              options={["All", ...filteredTechnologies]}
              value={selectedTechnology}
              onChange={handleTechnologyChange}
              placeholder="Technology"
              openCurrentDropdown={openTechnology}
              setOpenDropdown={setOpenTechnology}
              setOtherDropdown={setOpenIndustry}
            />
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  )
}

export default NewCarousel
